import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Checkbox,
  Grid,
  Box,
  Text,
  HStack,
  Divider,
  VStack,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';
import { ALL_PDF_COLUMNS, DEFAULT_SELECTED_COLUMNS } from '../../../../../utils/nfseTablePdfExport';

export type ExportMode = 'unificado' | 'separado';

interface ExportPdfModalProps {
  isOpen: boolean;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onExport: (columns: string[], mode: ExportMode) => void;
  isExporting: boolean;
  totalNotas: number;
}

export function ExportPdfModal({
  isOpen,
  onClose,
  onExport,
  isExporting,
  totalNotas,
}: ExportPdfModalProps) {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(DEFAULT_SELECTED_COLUMNS);
  const [exportMode, setExportMode] = useState<ExportMode>('unificado');

  const handleToggle = (key: string) => {
    setSelectedColumns(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  };

  const handleSelectAll = () => {
    setSelectedColumns(ALL_PDF_COLUMNS.map(c => c.key));
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
  };

  const handleReset = () => {
    setSelectedColumns(DEFAULT_SELECTED_COLUMNS);
  };

  // Agrupa colunas por categoria
  const groups = [
    {
      label: 'Dados da Nota',
      keys: ['data_emissao', 'data_competencia', 'numero_nfse', 'tipo', 'chave_nfse'],
    },
    {
      label: 'Emitente',
      keys: ['emissor_nome', 'emissor_cnpj', 'local_emissao', 'emissor_im'],
    },
    {
      label: 'Tomador',
      keys: ['tomador_nome', 'tomador_cnpj', 'tomador_municipio'],
    },
    {
      label: 'Valores',
      keys: ['valor_serv_prest', 'valor_bc', 'aliquota_aplic', 'local_incidencia'],
    },
    {
      label: 'Impostos / Retenções',
      keys: [
        'valor_issqn',
        'iss_retido',
        'irrf',
        'pis',
        'cofins',
        'tp_ret_pis_cofins',
        'csll',
        'inss',
      ],
    },
    {
      label: 'Serviço / Outros',
      keys: [
        'servico_codigo',
        'tributo_nacional',
        'discriminacao',
        'servico_codigo_trib_mun',
        'desc_trib_municipio',
      ],
    },
  ];

  const columnMap = Object.fromEntries(ALL_PDF_COLUMNS.map(c => [c.key, c]));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          Exportar PDF - Selecione as colunas
          <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>
            {totalNotas} nota(s) serão exportadas com os filtros atuais
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <HStack spacing={3} mb={4}>
            <Button size="xs" variant="outline" colorScheme="blue" onClick={handleSelectAll}>
              Selecionar todas
            </Button>
            <Button size="xs" variant="outline" colorScheme="gray" onClick={handleDeselectAll}>
              Desmarcar todas
            </Button>
            <Button size="xs" variant="outline" colorScheme="orange" onClick={handleReset}>
              Padrão
            </Button>
            <Text fontSize="xs" color="gray.500" ml="auto">
              {selectedColumns.length} de {ALL_PDF_COLUMNS.length} selecionadas
            </Text>
          </HStack>

          <Box mb={4} p={3} bg="gray.50" borderRadius="md">
            <Text fontSize="xs" fontWeight="bold" color="blue.600" mb={2} textTransform="uppercase">
              Modo de exportação
            </Text>
            <RadioGroup value={exportMode} onChange={v => setExportMode(v as ExportMode)}>
              <Stack direction="row" spacing={6}>
                <Radio value="unificado" size="sm">
                  <Text fontSize="xs">Unificado (todas as notas em 1 PDF)</Text>
                </Radio>
                <Radio value="separado" size="sm">
                  <Text fontSize="xs">Separado (1 PDF tomado + 1 PDF prestado)</Text>
                </Radio>
              </Stack>
            </RadioGroup>
          </Box>

          <VStack align="stretch" spacing={4}>
            {groups.map(group => (
              <Box key={group.label}>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="blue.600"
                  mb={2}
                  textTransform="uppercase"
                >
                  {group.label}
                </Text>
                <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                  {group.keys.map(key => {
                    const col = columnMap[key];
                    if (!col) return null;
                    return (
                      <Checkbox
                        key={key}
                        isChecked={selectedColumns.includes(key)}
                        onChange={() => handleToggle(key)}
                        size="sm"
                      >
                        <Text fontSize="xs">{col.label}</Text>
                      </Checkbox>
                    );
                  })}
                </Grid>
                <Divider mt={3} />
              </Box>
            ))}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} size="sm">
            Cancelar
          </Button>
          <Button
            colorScheme="red"
            onClick={() => onExport(selectedColumns, exportMode)}
            isLoading={isExporting}
            isDisabled={selectedColumns.length === 0}
            size="sm"
            leftIcon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            }
          >
            Exportar PDF
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
